import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Calendar, Tag, ArrowLeft } from 'lucide-react';
import { mockData } from '../mock';
import SEO from '../components/SEO';

const BlogPost = () => {
  const { slug } = useParams();
  const post = mockData.blogPosts.find(p => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Convert markdown-style content to HTML-like structure
  const renderContent = (content) => {
    return content.split('\n').map((line, index) => {
      // Headers
      if (line.startsWith('# ')) {
        return <h1 key={index} className="heading-1" style={{marginTop: '2rem', marginBottom: '1rem'}}>{line.substring(2)}</h1>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={index} className="heading-2" style={{marginTop: '2rem', marginBottom: '1rem'}}>{line.substring(3)}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={index} className="heading-3" style={{marginTop: '1.5rem', marginBottom: '0.75rem'}}>{line.substring(4)}</h3>;
      }
      
      // Bold text
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={index} className="body-medium" style={{fontWeight: 600, marginBottom: '0.5rem'}}>{line.substring(2, line.length - 2)}</p>;
      }
      
      // Horizontal rule
      if (line === '---') {
        return <hr key={index} style={{margin: '2rem 0', border: 'none', borderTop: '1px solid var(--border-light)'}} />;
      }
      
      // Empty lines
      if (line.trim() === '') {
        return <div key={index} style={{height: '0.5rem'}} />;
      }
      
      // Regular paragraphs
      return <p key={index} className="body-medium" style={{marginBottom: '1rem', lineHeight: 1.7}}>{line}</p>;
    });
  };

  return (
    <div className="blog-post-page">
      <SEO 
        title={`${post.title} | 12 Tribes Doula Services Blog`}
        description={post.excerpt}
        keywords={post.tags.join(', ')}
        url={`https://georgia-birth-coach.preview.emergentagent.com/blog/${post.slug}`}
        image={post.image}
        type="article"
        article={true}
        publishedDate={post.date}
        author={post.author}
      />
      {/* Header */}
      <section className="blog-post-header">
        <div className="container">
          <Link to="/blog" className="back-to-blog">
            <ArrowLeft size={16} />
            Back to Blog
          </Link>
          <div className="blog-post-category">{post.category}</div>
          <h1 className="heading-hero" style={{marginTop: '1rem'}}>{post.title}</h1>
          <div className="blog-post-meta">
            <span className="blog-meta-item">
              <Calendar size={16} />
              {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
            <span className="blog-meta-divider">•</span>
            <span className="blog-meta-item">By {post.author}</span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="blog-post-image-section">
        <div className="container">
          <div className="blog-post-featured-image">
            <img src={post.image} alt={post.title} />
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="blog-post-content-section">
        <div className="container">
          <article className="blog-post-content">
            {renderContent(post.content)}
          </article>

          {/* Tags */}
          <div className="blog-post-tags">
            <Tag size={16} />
            <span className="body-small" style={{fontWeight: 500}}>Tags:</span>
            {post.tags.map((tag, index) => (
              <span key={index} className="blog-tag">{tag}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Related CTA */}
      <section className="blog-post-cta">
        <div className="container">
          <div className="cta-box">
            <h3 className="heading-2">Ready to Experience Holistic Birth Support?</h3>
            <p className="body-medium">Schedule a free consultation to discuss your unique birth journey and how we can support you.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn-primary">Schedule Consultation</Link>
              <Link to="/services" className="btn-secondary">Explore Services</Link>
            </div>
          </div>
        </div>
      </section>

      {/* More Articles */}
      <section className="more-articles-section">
        <div className="container">
          <h3 className="heading-2" style={{textAlign: 'center', marginBottom: '2rem'}}>More Articles</h3>
          <div className="more-articles-grid">
            {mockData.blogPosts
              .filter(p => p.id !== post.id)
              .slice(0, 2)
              .map((article) => (
                <Link key={article.id} to={`/blog/${article.slug}`} className="more-article-card">
                  <div className="more-article-image">
                    <img src={article.image} alt={article.title} />
                  </div>
                  <div className="more-article-content">
                    <p className="caption">{article.category}</p>
                    <h4 className="body-medium" style={{fontWeight: 500, marginTop: '0.5rem'}}>{article.title}</h4>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
